import { create } from "zustand";
import { userService } from "../services/userService";
import {
  CreateUserRequest,
  UpdateUserRequest,
  ApiError,
  SearchUsersRequest,
  UserListItem,
  UserSearchStatus,
} from "../types/api";
import {
  INITIAL_USER_ADVANCED_FILTERS,
  mapAdvancedFiltersToSearchRequest,
  type UserAdvancedFilters,
} from "../pages/users/userSearchFilters";

export interface UserListFilters {
  gender: string;
  status: string;
  paid: string;
  advanced: UserAdvancedFilters;
}

export type UserListFiltersPatch = Partial<
  Pick<UserListFilters, "gender" | "status" | "paid">
> & {
  advanced?: Partial<UserAdvancedFilters> | UserAdvancedFilters;
};

const buildSearchBody = (
  page: number,
  limit: number,
  searchQuery: string,
  filters: UserListFilters
): SearchUsersRequest => {
  const body: SearchUsersRequest = { page, limit };
  const trimmed = searchQuery.trim();
  if (trimmed) {
    body.search = trimmed;
  }
  if (filters.gender) {
    body.gender = filters.gender;
  }
  if (filters.status) {
    body.status =
      filters.status === "pending"
        ? "pending_verification"
        : (filters.status as UserSearchStatus);
  }
  if (filters.paid === "paid") {
    body.is_paid = true;
  } else if (filters.paid === "unpaid") {
    body.is_paid = false;
  }

  Object.assign(body, mapAdvancedFiltersToSearchRequest(filters.advanced));

  return body;
};

interface UserState {
  users: UserListItem[];
  currentPage: number;
  pageSize: number;
  total: number;
  totalPages: number;
  searchQuery: string;
  filters: UserListFilters;
  isLoading: boolean;
  blockingUserId: string | null;
  invitingUserId: string | null;
  hidingUserId: string | null;
  error: ApiError | null;

  fetchUsers: (page?: number, limit?: number) => Promise<void>;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setFilters: (patch: UserListFiltersPatch) => Promise<void>;
  clearFilters: () => Promise<void>;
  searchUsers: (query: string) => Promise<void>;
  blockUser: (userId: string) => Promise<void>;
  unblockUser: (userId: string) => Promise<void>;
  sendInviteEmail: (userId: string) => Promise<void>;
  createUser: (userData: CreateUserRequest) => Promise<void>;
  updateUser: (id: string, userData: UpdateUserRequest) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  hideUser: (userId: string) => Promise<void>;
  unhideUser: (userId: string) => Promise<void>;
  clearError: () => void;
}

export const useUserStore = create<UserState>()((set, get) => ({
  users: [],
  currentPage: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
  searchQuery: "",
  filters: {
    gender: "",
    status: "",
    paid: "",
    advanced: { ...INITIAL_USER_ADVANCED_FILTERS },
  },
  isLoading: false,

  blockingUserId: null,
  invitingUserId: null,
  hidingUserId: null,
  error: null,

  fetchUsers: async (page?: number, limit?: number) => {
    const currentPage = page ?? get().currentPage;
    const pageSize = limit ?? get().pageSize;
    const { searchQuery, filters } = get();

    set({ isLoading: true, error: null });
    try {
      const response = await userService.searchUsers(
        buildSearchBody(currentPage, pageSize, searchQuery, filters)
      );

      set({
        users: response.data,
        currentPage: response.metaData.page,
        pageSize: response.metaData.limit,
        total: response.metaData.total,
        totalPages: response.metaData.totalPages,
        isLoading: false,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  setPage: (page: number) => {
    set({ currentPage: page });
    get().fetchUsers(page);
  },

  setPageSize: (size: number) => {
    set({ pageSize: size, currentPage: 1 });
    get().fetchUsers(1, size);
  },

  setFilters: async (patch: UserListFiltersPatch) => {
    const current = get().filters;
    const { advanced: advancedPatch, ...toolbarPatch } = patch;

    set({
      filters: {
        ...current,
        ...toolbarPatch,
        advanced: advancedPatch
          ? { ...current.advanced, ...advancedPatch }
          : current.advanced,
      },
      currentPage: 1,
    });
    await get().fetchUsers(1);
  },

  clearFilters: async () => {
    set({
      searchQuery: '',
      currentPage: 1,
      filters: {
        gender: '',
        status: '',
        paid: '',
        advanced: { ...INITIAL_USER_ADVANCED_FILTERS },
      },
    });
    await get().fetchUsers(1);
  },

  searchUsers: async (query: string) => {
    set({ searchQuery: query, currentPage: 1 });
    await get().fetchUsers(1);
  },

  blockUser: async (userId: string) => {
    set({ blockingUserId: userId });
    try {
      await userService.blockUser(userId);
      set({
        users: get().users.map((user) =>
          user.userId === userId
            ? { ...user, isBlocked: true, status: 'blocked' }
            : user
        ),
      });
      await get().fetchUsers(get().currentPage);
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ blockingUserId: null });
    }
  },

  unblockUser: async (userId: string) => {
    set({ blockingUserId: userId });
    try {
      await userService.unblockUser(userId);
      set({
        users: get().users.map((user) =>
          user.userId === userId
            ? {
                ...user,
                isBlocked: false,
                status: user.status === 'blocked' ? 'active' : user.status,
              }
            : user
        ),
      });
      await get().fetchUsers(get().currentPage);
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ blockingUserId: null });
    }
  },

  sendInviteEmail: async (userId: string) => {
    set({ invitingUserId: userId, error: null });
    try {
      await userService.sendInvite(userId);
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ invitingUserId: null });
    }
  },

  createUser: async (userData: CreateUserRequest) => {
    set({ isLoading: true, error: null });
    try {
      await userService.createUser(userData);
      await get().fetchUsers(get().currentPage);
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  updateUser: async (id: string, userData: UpdateUserRequest) => {
    set({ isLoading: true, error: null });
    try {
      await userService.updateUser(id, userData);
      await get().fetchUsers(get().currentPage);
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  deleteUser: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await userService.deleteUser(id);
      await get().fetchUsers(get().currentPage);
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  hideUser: async (userId: string) => {
    set({ hidingUserId: userId });
    try {
      await userService.hideUser(userId);
      await get().fetchUsers(get().currentPage);
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ hidingUserId: null });
    }
  },

  unhideUser: async (userId: string) => {
    set({ hidingUserId: userId });
    try {
      await userService.unhideUser(userId);
      await get().fetchUsers(get().currentPage);
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ hidingUserId: null });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
